import { Link, useNavigate } from "@remix-run/react";
import { useState, useEffect } from "react";
import { useTranslation } from "~/shared/utils/i18n";
import type { MetaFunction } from "@remix-run/node";
import { getAllKeywords, createSEODescription } from "~/shared/utils/seo-keywords";
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/shared/components/performance.css";
import "~/shared/components/mobile-performance.css";
import "~/features/resume/resume-design-principles.css";
import "~/features/resume/section-order.css";
import "~/features/resume/layout-tab.css";
import "~/features/resume/education-tab.css";


import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCalculator, 
  FaCalendarAlt, 
  FaFont, 
  FaWhatsapp, 
  FaFileAlt, 
  FaGift,
  FaCog,
  FaHeart,
  FaArrowLeft,
  FaArrowRight,
  FaUser,
  FaGlobe,
  FaSun,
  FaMoon,
  FaTools,
  FaEllipsisH,
  FaQrcode,
  FaPray,
  FaSquareRootAlt,
  FaFlag,
  FaLightbulb,
  FaReceipt,
  FaExchangeAlt,
  FaChartBar,
  FaEnvelope
} from 'react-icons/fa';
import { AiFillHome } from 'react-icons/ai';

// ============================================================================
// SEO METADATA - تحسين محركات البحث للصفحة الرئيسية
// ============================================================================

export const meta: MetaFunction = () => {
  const allKeywords = getAllKeywords();
  const keywordsString = allKeywords.join(', ');
  const description = createSEODescription();
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "أسامة الزبيدي - الصفحة الرئيسية | محلل بيانات، مطور ويب، متخصص رياضيات | Osama Al-Zubaidi - Homepage" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية الشاملة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    { name: "geo.placename", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "أسامة الزبيدي - الصفحة الرئيسية | محلل بيانات، مطور ويب، متخصص رياضيات" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "أسامة الزبيدي - الصفحة الرئيسية" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "أسامة الزبيدي - الصفحة الرئيسية | محلل بيانات، مطور ويب، متخصص رياضيات" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "أسامة الزبيدي - الصفحة الرئيسية" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa" },
    { name: "alternate", content: "https://oz.sa", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// تم إزالة mainPageTranslations غير المستخدم

// Unified color scheme matching the resume app design
const widgetColors = {
  primary: {
    light: 'bg-gradient-to-br from-purple-500 to-purple-600',
    dark: 'dark:from-purple-600 dark:to-purple-700',
    text: 'text-white',
    hover: 'hover:from-purple-600 hover:to-purple-700',
    shadow: 'shadow-purple-500/20'
  },
  secondary: {
    light: 'bg-gradient-to-br from-pink-500 to-pink-600',
    dark: 'dark:from-pink-600 dark:to-pink-700',
    text: 'text-white',
    hover: 'hover:from-pink-600 hover:to-pink-700',
    shadow: 'shadow-pink-500/20'
  },
  success: {
    light: 'bg-gradient-to-br from-green-500 to-green-600',
    dark: 'dark:from-green-600 dark:to-green-700',
    text: 'text-white',
    hover: 'hover:from-green-600 hover:to-green-700',
    shadow: 'shadow-green-500/20'
  },
  warning: {
    light: 'bg-gradient-to-br from-orange-500 to-orange-600',
    dark: 'dark:from-orange-600 dark:to-orange-700',
    text: 'text-white',
    hover: 'hover:from-orange-600 hover:to-orange-700',
    shadow: 'shadow-orange-500/20'
  },
  danger: {
    light: 'bg-gradient-to-br from-red-500 to-red-600',
    dark: 'dark:from-red-600 dark:to-red-700',
    text: 'text-white',
    hover: 'hover:from-red-600 hover:to-red-700',
    shadow: 'shadow-red-500/20'
  },
  info: {
    light: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
    dark: 'dark:from-indigo-600 dark:to-indigo-700',
    text: 'text-white',
    hover: 'hover:from-indigo-600 hover:to-indigo-700',
    shadow: 'shadow-indigo-500/20'
  },
  dark: {
    light: 'bg-gradient-to-br from-gray-800 to-gray-900',
    dark: 'dark:from-gray-700 dark:to-gray-800',
    text: 'text-white',
    hover: 'hover:from-gray-700 hover:to-gray-800',
    shadow: 'shadow-gray-500/20'
  }
};

// Icon component interface
// تم إزالة IconProps و IconComponent غير المستخدمة

// تم إزالة الأيقونات غير المستخدمة - نستخدم WidgetIcon بدلاً منها

// تم إزالة WidgetConfig غير المستخدم

// تكوين الخدمات - مماثل لصفحة الخدمات
const widgetCards = [
  {
    title: "بطاقات التهنئة",
    description: "إنشاء بطاقات تهنئة بأسهل طريقة",
    icon: "gift",
    route: "/greeting-cards",
    category: "creative",
    featured: true
  },
  {
    title: "السيرة الذاتية الذكية",
    description: "إنشاء السيرة الذاتية الذكية بأسهل طريقة",
    icon: "resume",
    route: "/resume",
    category: "professional",
    featured: true
  },
  {
    title: "مواعيد السعودية",
    description: "الوقت المتبقي للتواريخ المهمة في المملكة",
    icon: "saudi",
    route: "/saudi-dates",
    category: "islamic",
    featured: true
  },
  {
    title: "حاسبة العمر",
    description: "احسب عمرك بالتقويم الميلادي والهجري مع معلومات إضافية",
    icon: "calculator",
    route: "/age-calculator",
    category: "tools"
  },
  {
    title: "حاسبة الفرق بين التواريخ",
    description: "احسب الفرق بين أي تاريخين بالتقويم الميلادي والهجري",
    icon: "calendar",
    route: "/date-difference",
    category: "tools"
  },
  {
    title: "تفقيط الأعداد",
    description: "تحويل الأعداد إلى نصوص مكتوبة",
    icon: "font",
    route: "/tafqit",
    category: "tools"
  },
  {
    title: "واتس مباشر",
    description: "إرسال رسائل سريعة إلى أي جهاز يعمل بنظام التشغيل Android",
    icon: "whatsapp",
    route: "/quick-whats",
    category: "communication"
  },
  {
    title: "مولد رمز الاستجابة السريعة",
    description: "إنشاء رمز الاستجابة السريعة بأسهل طريقة",
    icon: "qr",
    route: "/qr-generator",
    category: "tools"
  },
  {
    title: "أذكاري",
    description: "أذكار الصباح والمساء وأذكار أخرى",
    icon: "prayer",
    route: "/adhkar",
    category: "islamic",
    featured: true
  },
  {
    title: "مولد الفواتير",
    description: "إنشاء فواتير احترافية بسهولة",
    icon: "invoice",
    route: "/invoice",
    category: "professional"
  },
  {
    title: "محول التاريخ",
    description: "تحويل التاريخ بين الهجري والميلادي بسهولة",
    icon: "exchange",
    route: "/date-converter",
    category: "tools",
    featured: false
  },
  {
    title: "تاريخ اليوم",
    description: "التاريخ الهجري والميلادي مع الوقت الحالي",
    icon: "calendar",
    route: "/today-date",
    category: "tools",
    featured: true
  },
  {
    title: "محلل البيانات",
    description: "حلل البيانات وحول JSON/CSV إلى أكواد مُكتوبة تلقائياً",
    icon: "analytics",
    route: "/data-analytics",
    category: "tools",
    featured: true
  },
  {
    title: "البريد المؤقت",
    description: "إنشاء بريد مؤقت لاستقبال الرسائل بدون كشف هويتك",
    icon: "email",
    route: "/temp-mail",
    category: "tools",
    featured: true
  },
  {
    title: "المزيد",
    description: "استكشف المزيد من الخدمات والأدوات",
    icon: "more",
    route: "/services",
    category: "all",
    featured: false
  },

];

// بيانات الإحصائيات والتصفية - مماثل لصفحة الخدمات

// تكوين الخدمات مع الألوان والأحجام - نوعين فقط (كبير وصغير)
const widgetServiceConfigs = [
  {
    id: 'greeting-cards',
    colorScheme: 'danger' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'resume',
    colorScheme: 'primary' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'saudi-dates',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'age-calculator',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'date-difference',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'tafqit',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'whatsapp',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'qr-generator',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'adhkar',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'date-converter',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const
  },
  {
    id: 'today-date',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'large' as const
  },
  {
    id: 'data-analytics',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'temp-mail',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'invoice',
    colorScheme: 'primary' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'more',
    colorScheme: 'secondary' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },

];

// تم إزالة widgetConfigs المكرر - نستخدم widgetCards فقط

// تم إزالة WidgetProps غير المستخدم

// تم إزالة الدوال غير المستخدمة

// Replace ProductShowcase with a MyStore
function MyStore() {
  const products = [
    {
      id: 'img4',
      title: 'تصميم متاجر إلكترونية',
      price: '150 ريال ',
      priceSup: 'أبتدء من',
      bg: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405223/starwars/item-4-bg.webp',
      img: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405215/starwars/item-4.webp',
      logo: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405214/starwars/logo.webp',
      sizes: ['شوبفاير', 'زد+', 'منصة سلة', 'موقع'],
      icons: [
        <svg key="shopify" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>,
        <svg key="zid" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
        </svg>,
        <svg key="cart" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
        </svg>,
        <svg key="website" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      ],
      durability: 80,
      durabilityLabel: 'الميزات',
      sizeLabel: 'المنصات',
    },
    {
      id: 'img1',
      title: `تصميم منشورات\nبطاقات تهنئة`,
      price: '49 ريال ',
      priceSup: 'أبتدء من',
      bg: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405222/starwars/item-1-bg.webp',
      img: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405217/starwars/item-1.webp',
      logo: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405214/starwars/logo.webp',
      sizes: ['Ps', 'Ai'],
      icons: [
        <svg key="photoshop" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>,
        <svg key="illustrator" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ],
      durability: 75,
      durabilityLabel: 'الميزات',
      sizeLabel: 'البرامج',
    },
    {
      id: 'img2',
      title: `تخطيط وتصميم\nمواقع إلكترونية`,
      price: '250 ريال ',
      priceSup: 'أبتدء من',
      bg: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405222/starwars/item-2-bg.webp',
      img: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405217/starwars/item-2.webp',
      logo: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405214/starwars/logo.webp',
      sizes: ['Xd', 'Figma', 'Framer'],
      icons: [
        <svg key="xd" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>,
        <svg key="figma" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>,
        <svg key="framer" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ],
      durability: 80,
      durabilityLabel: 'الميزات',
      sizeLabel: 'البرامج',
    },
    {
      id: 'img3',
      title: `تصميم تطبيقات\nموبايل`,
      price: '350 ريال ',
      priceSup: 'أبتدء من',
      bg: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405215/starwars/item-3-bg.webp',
      img: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405218/starwars/item-3.webp',
      logo: 'https://res.cloudinary.com/muhammederdem/image/upload/q_60/v1536405214/starwars/logo.webp',
      sizes: ['iOS', 'Android', 'React'],
      icons: [
        <svg key="ios" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>,
        <svg key="android" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.6 11.48c.88 0 1.6-.72 1.6-1.6s-.72-1.6-1.6-1.6-1.6.72-1.6 1.6.72 1.6 1.6 1.6zm-11.2 0c.88 0 1.6-.72 1.6-1.6s-.72-1.6-1.6-1.6-1.6.72-1.6 1.6.72 1.6 1.6 1.6zm5.6-8.8c-3.2 0-5.8 2.6-5.8 5.8v1.6H4.8v8h14.4v-8h-2.4V8.48c0-3.2-2.6-5.8-5.8-5.8z"/>
        </svg>,
        <svg key="react" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ],
      durability: 85,
      durabilityLabel: 'الميزات',
      sizeLabel: 'المنصات',
    },
  ];
  const [active, setActive] = useState(0);
  const [wishlist, setWishlist] = useState(Array(products.length).fill(false));
  const [selectedSize, setSelectedSize] = useState(products.map(p => p.sizes[1] || p.sizes[0]));

  const cardVariants = {
    initial: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
      scale: 0.98
    }),
    animate: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', stiffness: 80, damping: 20 }
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
      scale: 0.98,
      transition: { duration: 0.25 }
    })
  };
  const imageVariants = {
    initial: { scale: 0.85, opacity: 0, rotate: -8 },
    animate: { scale: 1, opacity: 1, rotate: 0, transition: { type: 'spring', stiffness: 120, damping: 18 } },
    exit: { scale: 0.85, opacity: 0, rotate: 8, transition: { duration: 0.25 } }
  };
  const [direction, setDirection] = useState(0);
  const handlePrev = () => { setDirection(-1); setActive(a => (a > 0 ? a - 1 : a)); };
  const handleNext = () => { setDirection(1); setActive(a => (a < products.length - 1 ? a + 1 : a)); };
  const handleWishlist = (idx: number) => setWishlist(w => w.map((v, i) => (i === idx ? !v : v)));
  const handleSize = (idx: number, size: string) => setSelectedSize(s => s.map((v, i) => (i === idx ? size : v)));

  const product = products[active];

  return (
    <section className="w-full flex justify-center items-center py-32 sm:py-36 md:py-40 lg:py-48 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-600 overflow-hidden relative">
      {/* Animated Background Elements - Matching Hero Section */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Top gradient transition - 10% of section height */}
        <div className="absolute top-0 left-0 w-full h-[10%] bg-gradient-to-b from-white/90 via-indigo-50/80 to-transparent dark:from-slate-800 dark:via-indigo-900/80 dark:to-transparent pointer-events-none" />
        
        {/* Floating orbs with blur effect */}
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-20 w-32 h-32 bg-purple-400/30 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute top-40 right-32 w-24 h-24 bg-pink-400/40 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, 60, 0],
            y: [0, -40, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
          className="absolute bottom-32 left-1/4 w-28 h-28 bg-blue-400/35 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, -40, 0],
            y: [0, 80, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 6
          }}
          className="absolute bottom-20 right-1/3 w-20 h-20 bg-indigo-400/45 rounded-full blur-xl"
        />
        {/* Gradient overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/20" />
        
        {/* Bottom gradient transition - 10% of section height */}
        <div className="absolute bottom-0 left-0 w-full h-[10%] bg-gradient-to-t from-white/90 via-indigo-50/80 to-transparent dark:from-slate-800 dark:via-indigo-900/80 dark:to-transparent pointer-events-none" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Desktop Layout (lg and above) */}
        <div className="hidden lg:block relative min-h-[500px] lg:min-h-[600px] xl:min-h-[700px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={product.id}
              custom={direction}
              variants={cardVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="contents"
              transition={{ duration: 0.4 }}
            >
              {/* Left Card - Updated to match app theme */}
              <div className="absolute left-12 top-1/2 -translate-y-1/2 w-[35%] h-[500px] lg:h-[600px] xl:h-[700px] bg-gradient-to-br from-indigo-600/90 via-purple-600/80 to-indigo-700/90 backdrop-blur-lg rounded-3xl z-10 flex items-center justify-center overflow-hidden border border-white/10 shadow-2xl">
                <span className="absolute left-1 top-1/1 -translate-y-1/3 text-[8rem] lg:text-[10rem] xl:text-[12rem] font-extrabold text-white/20 select-none blur-sm" style={{letterSpacing:'-0.1em', writingMode: 'vertical-lr', textOrientation: 'mixed', transform: 'rotate(180deg)', textShadow: '0 0 30px rgba(255,255,255,0.3), 0 0 60px rgba(255,255,255,0.2), 0 0 90px rgba(255,255,255,0.1)'}}>
                  خدمة<br/>أطلب
                </span>
              </div>
              
              {/* Right Card - Updated to match app theme */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[70%] h-[500px] lg:h-[600px] xl:h-[700px] bg-gradient-to-br from-slate-900/95 via-indigo-900/90 to-slate-800/95 backdrop-blur-lg rounded-3xl z-10 flex items-center justify-center overflow-hidden border border-white/10 shadow-2xl">
                <img src={product.bg} alt="bg" className="absolute inset-0 w-full h-full object-cover opacity-20" />
              </div>
              
              {/* Product Image Overlapping Both Cards */}
              <motion.div
                className="absolute left-[18%] top-1/4 -translate-y-1/2 z-20 w-[300px] lg:w-[350px] xl:w-[400px] h-[300px] lg:h-[350px] xl:h-[400px] flex items-center justify-center"
                variants={imageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                key={product.img}
              >
                <img
                  src={product.img}
                  alt={product.title}
                  className="w-full h-full object-contain drop-shadow-2xl"
                  style={{filter:'drop-shadow(0 8px 32px #0008)'}}
                />
              </motion.div>
              
              {/* Product Content (right card) */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-[60%] h-[500px] lg:h-[600px] xl:h-[700px] flex flex-col justify-center px-12 lg:px-16 xl:px-20">
                <h2 className="text-white font-extrabold text-3xl lg:text-4xl xl:text-5xl mb-4 whitespace-pre-line drop-shadow-lg">{product.title}</h2>
                <div className="text-white text-2xl lg:text-3xl xl:text-4xl font-bold mb-8 drop-shadow-lg"><span className="text-lg align-super text-indigo-200">{product.priceSup} </span>{product.price}</div>
                <div className="flex items-center gap-8 mb-8">
                  {/* Platform/Software Options */}
                  <div>
                    <div className="text-indigo-200 font-bold tracking-widest text-xs mb-3">{product.sizeLabel}</div>
                    <div className="flex gap-4 flex-wrap">
                      {product.sizes.map((size, index) => (
                        <button
                          key={size}
                          onClick={() => handleSize(active, size)}
                          className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold text-xs lg:text-sm transition-all duration-300 ${
                            selectedSize[active] === size
                              ? 'border-indigo-400 text-indigo-400 bg-white/90 scale-110 shadow-lg shadow-indigo-500/25'
                              : 'border-slate-400 text-slate-300 bg-transparent hover:border-indigo-400 hover:scale-105 hover:bg-white/10'
                          }`}
                          title={size}
                        >
                          {product.icons && product.icons[index] ? product.icons[index] : size}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="h-16 w-px bg-white/20 mx-4" />
                  {/* Features Rate */}
                  <motion.div className="flex flex-col items-center" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 1.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}>
                    <svg width="70" height="70">
                      <circle
                        cx="35"
                        cy="35"
                        r="30"
                        stroke="#6366f1"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray={2 * Math.PI * 30}
                        strokeDashoffset={2 * Math.PI * 30 * (1 - product.durability / 100)}
                        style={{ transition: 'stroke-dashoffset 1s' }}
                      />
                    </svg>
                    <div className="absolute text-indigo-400 font-bold text-lg" style={{ marginTop: "-2.5rem" }}>{product.durability}%</div>
                    <div className="text-xs text-indigo-200 mt-2 font-bold tracking-widest">{product.durabilityLabel}</div>
                  </motion.div>
                </div>
                {/* Add to Cart and Wishlist */}
                <div className="flex items-center gap-6 mt-6">
                  <button className="px-10 py-4 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-bold text-lg transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 shadow-indigo-500/25">
                    تواصل معي
                  </button>
                  <motion.button
                    className="flex items-center gap-3 text-indigo-200 hover:text-indigo-400 transition-all duration-300"
                    onClick={() => handleWishlist(active)}
                    whileTap={{ scale: 1.2 }}
                  >
                    
                    <span className="text-sm font-bold">يصنع لك بكل</span>
                    <FaHeart className={wishlist[active] ? "text-indigo-400 animate-pulse" : ""} />
                  </motion.button>
                </div>
              </div>
              
              {/* Navigation Arrows (outside cards) */}
              <button
                className={`absolute left-[-50px] top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-lg text-slate-800 rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300 z-40 hover:scale-110 active:scale-95 border border-white/20 ${active === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handlePrev}
                disabled={active === 0}
              >
                <FaArrowLeft />
              </button>
              <button
                className={`absolute right-[-50px] top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-lg text-slate-800 rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300 z-40 hover:scale-110 active:scale-95 border border-white/20 ${active === products.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleNext}
                disabled={active === products.length - 1}
              >
                <FaArrowRight />
              </button>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile/Tablet Layout (below lg) */}
        <div className="lg:hidden relative min-h-[600px] sm:min-h-[700px] md:min-h-[800px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={product.id}
              custom={direction}
              variants={cardVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="contents"
              transition={{ duration: 0.4 }}
            >
              {/* Top Card - Updated to match app theme */}
              <div className="absolute left-0 top-0 w-full h-32 sm:h-40 md:h-48 bg-gradient-to-br from-indigo-600/90 via-purple-600/80 to-indigo-700/90 backdrop-blur-lg rounded-t-2xl sm:rounded-t-3xl z-10 flex items-center justify-center overflow-hidden border border-white/10 shadow-2xl">
                <span className="text-white/20 text-2xl sm:text-3xl md:text-4xl font-extrabold select-none">أطلب خدمة</span>
              </div>
              
              {/* Bottom Card - Updated to match app theme */}
              <div className="absolute left-0 top-32 sm:top-40 md:top-48 w-full h-[500px] sm:h-[600px] md:h-[700px] bg-gradient-to-br from-slate-900/95 via-indigo-900/90 to-slate-800/95 backdrop-blur-lg rounded-b-2xl sm:rounded-b-3xl z-10 flex items-center justify-center overflow-hidden border border-white/10 shadow-2xl">
                <img src={product.bg} alt="bg" className="absolute inset-0 w-full h-full object-cover opacity-20" />
              </div>
              
              {/* Product Image (centered) */}
              <motion.div
                className="absolute left-1/3 top-16 sm:top-20 md:top-24 -translate-x-1/2 z-20 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 flex items-center justify-center"
                variants={imageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                key={product.img}
              >
                <img
                  src={product.img}
                  alt={product.title}
                  className="w-full h-full object-contain drop-shadow-2xl"
                  style={{filter:'drop-shadow(0 8px 32px #0008)'}}
                />
              </motion.div>
              
              {/* Product Content (below image) */}
              <div className="absolute left-0 top-48 sm:top-56 md:top-64 w-full h-auto flex flex-col justify-start px-6 sm:px-8 md:px-12 pt-8 sm:pt-12 md:pt-16 z-30">
                <h2 className="text-white font-extrabold text-xl sm:text-2xl md:text-3xl mb-3 sm:mb-4 whitespace-pre-line text-center drop-shadow-lg">{product.title}</h2>
                <div className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-6 sm:mb-8 text-center drop-shadow-lg"><span className="text-base align-super text-indigo-200">{product.priceSup} </span>{product.price}</div>
                
                <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 mb-6 sm:mb-8">
                  {/* Platform/Software Options */}
                  <div className="w-full sm:w-auto">
                    <div className="text-indigo-200 font-bold tracking-widest text-xs mb-3 text-center sm:text-left">{product.sizeLabel}</div>
                    <div className="flex gap-3 sm:gap-4 flex-wrap justify-center sm:justify-start">
                      {product.sizes.map((size, index) => (
                        <button
                          key={size}
                          onClick={() => handleSize(active, size)}
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-300 ${
                            selectedSize[active] === size
                              ? 'border-indigo-400 text-indigo-400 bg-white/90 scale-110 shadow-lg shadow-indigo-500/25'
                              : 'border-slate-400 text-slate-300 bg-transparent hover:border-indigo-400 hover:scale-105 hover:bg-white/10'
                          }`}
                          title={size}
                        >
                          {product.icons && product.icons[index] ? product.icons[index] : size}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Features Rate */}
                  <motion.div className="flex flex-col items-center" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 1.2, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}>
                    <svg width="60" height="60" className="mb-1">
                      <circle
                        cx="30"
                        cy="30"
                        r="25"
                        stroke="#6366f1"
                        strokeWidth="4"
                        fill="none"
                        strokeDasharray={2 * Math.PI * 25}
                        strokeDashoffset={2 * Math.PI * 25 * (1 - product.durability / 100)}
                        style={{ transition: 'stroke-dashoffset 1s' }}
                      />
                    </svg>
                    <div className="text-indigo-400 font-bold text-base">{product.durability}%</div>
                    <div className="text-xs text-indigo-200 mt-1 font-bold tracking-widest">{product.durabilityLabel}</div>
                  </motion.div>
                </div>
                
                {/* Add to Cart and Wishlist */}
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mt-4 sm:mt-6 justify-center">
                  <button className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-bold text-base sm:text-lg transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 shadow-indigo-500/25">
                    تواصل معي
                  </button>
                  <motion.button
                    className="flex items-center gap-2 text-indigo-200 hover:text-indigo-400 transition-all duration-300"
                    onClick={() => handleWishlist(active)}
                    whileTap={{ scale: 1.2 }}
                  >
                    
                    <span className="text-xs sm:text-sm font-bold">يصنع لك بكل</span>
                    <FaHeart className={wishlist[active] ? "text-indigo-400 animate-pulse" : ""} />
                  </motion.button>
                </div>
              </div>
              
              {/* Navigation Arrows (closer to cards on mobile) */}
              <button
                className={`absolute left-4 sm:left-6 md:left-8 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-lg text-slate-800 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300 z-40 hover:scale-110 active:scale-95 border border-white/20 ${active === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handlePrev}
                disabled={active === 0}
              >
                <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                className={`absolute right-4 sm:right-6 md:right-8 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-lg text-slate-800 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300 z-40 hover:scale-110 active:scale-95 border border-white/20 ${active === products.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={handleNext}
                disabled={active === products.length - 1}
              >
                <FaArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// مكون الأيقونة المحسن - مماثل لصفحة الخدمات
function WidgetIcon({ icon, className = '' }: { icon: string; className?: string }) {
  const iconClasses = `w-full h-full flex items-center justify-center ${className}`;
  
  switch (icon) {
    case 'exchange':
      return <div className={iconClasses}><FaExchangeAlt className="w-4/5 h-4/5" /></div>;
    case 'calculator':
      return <div className={iconClasses}><FaCalculator className="w-4/5 h-4/5" /></div>;
    case 'calendar':
      return <div className={iconClasses}><FaCalendarAlt className="w-4/5 h-4/5" /></div>;
    case 'font':
      return <div className={iconClasses}><FaFont className="w-4/5 h-4/5" /></div>;
    case 'whatsapp':
      return <div className={iconClasses}><FaWhatsapp className="w-4/5 h-4/5" /></div>;
    case 'qr':
      return <div className={iconClasses}><FaQrcode className="w-4/5 h-4/5" /></div>;
    case 'resume':
      return <div className={iconClasses}><FaFileAlt className="w-4/5 h-4/5" /></div>;
    case 'gift':
      return <div className={iconClasses}><FaGift className="w-4/5 h-4/5" /></div>;
    case 'prayer':
      return <div className={iconClasses}><FaPray className="w-4/5 h-4/5" /></div>;
    case 'math':
      return <div className={iconClasses}><FaSquareRootAlt className="w-4/5 h-4/5" /></div>;
    case 'saudi':
      return <div className={iconClasses}><FaFlag className="w-4/5 h-4/5" /></div>;
    case 'invoice':
      return <div className={iconClasses}><FaReceipt className="w-4/5 h-4/5" /></div>;
    case 'analytics':
      return <div className={iconClasses}><FaChartBar className="w-4/5 h-4/5" /></div>;
    case 'email':
      return <div className={iconClasses}><FaEnvelope className="w-4/5 h-4/5" /></div>;
    case 'more':
      return <div className={iconClasses}><FaEllipsisH className="w-4/5 h-4/5" /></div>;
    default:
      return <div className={iconClasses}><FaTools className="w-4/5 h-4/5" /></div>;
  }
}

// مكون البطاقة المحسن - مماثل لصفحة الخدمات بالضبط
function WidgetCard({ card, config, lang, index }: {
  card: {
    title: string;
    description: string;
    icon: string;
    route: string;
    category?: string;
    featured?: boolean;
    disabled?: boolean;
  };
  config: {
    colorScheme: keyof typeof widgetColors;
    size: 'small' | 'large';
    disabled?: boolean;
  };
  lang: 'ar' | 'en';
  index: number;
}) {
  const colors = widgetColors[config.colorScheme];
  
  // تحديد حجم البطاقة - نوعين فقط (كبير وصغير) للتوافق مع الهوية البصرية
  const getWidgetSize = () => {
    switch (config.size) {
      case 'large':
        return {
          iconSize: 'w-12 h-12 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-20 lg:h-20 xl:w-22 xl:h-22',
          titleSize: 'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl',
          descriptionSize: 'text-sm sm:text-base md:text-lg lg:text-xl',
          padding: 'p-6 sm:p-8 md:p-10 lg:p-12 xl:p-14',
          iconMargin: 'mb-4 sm:mb-5 md:mb-6 lg:mb-8'
        };
      default: // small
        return {
          iconSize: 'w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16',
          titleSize: 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl',
          descriptionSize: 'text-xs sm:text-sm md:text-base lg:text-lg',
          padding: 'p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10',
          iconMargin: 'mb-3 sm:mb-4 md:mb-5 lg:mb-6'
        };
    }
  };

  const sizes = getWidgetSize();
  const isDisabled = config.disabled || card.disabled;

  if (isDisabled) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 0.5, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`
          relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600
          bg-gray-100 dark:bg-gray-800 cursor-not-allowed
          ${config.size === 'large' ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
          ${sizes.padding}
        `}
      >
        {/* Featured Badge for Disabled Cards */}
        {card.featured && (
          <div className="absolute top-2 right-2 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 px-2 py-1 rounded-full text-xs font-bold z-10 opacity-50">
            ✨ {lang === 'ar' ? 'مميز' : 'Featured'}
          </div>
        )}
        
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className={`${sizes.iconSize} ${sizes.iconMargin} text-gray-400 dark:text-gray-500`}>
            <WidgetIcon icon={card.icon} />
          </div>
          <h3 className={`${sizes.titleSize} font-bold text-gray-400 dark:text-gray-500 mb-2`}>
            {card.title}
          </h3>
          <p className={`${sizes.descriptionSize} text-gray-400 dark:text-gray-500`}>
            {card.description}
          </p>
          <div className="mt-4 px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-full">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {lang === 'ar' ? 'قريباً' : 'Coming Soon'}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <Link 
      to={card.route} 
      prefetch="intent"
      className={`
        block relative overflow-hidden rounded-2xl cursor-pointer group card-mobile-optimized touch-optimized
        ${colors.light} ${colors.dark} ${colors.text}
        ${config.size === 'large' ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
        ${sizes.padding}
        transition-all duration-300 ease-out
        hover:shadow-2xl ${colors.shadow}
        ${colors.hover}
        transform hover:scale-[1.02] hover:-translate-y-1
        active:scale-[0.98] active:translate-y-0
        focus:outline-none focus:ring-4 focus:ring-white/20
        touch-manipulation
        ${card.featured ? 'ring-2 ring-yellow-400/30 ring-offset-2 ring-offset-transparent' : ''}
      `}
      style={{ 
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`flex flex-col items-center justify-center h-full text-center min-h-full relative z-10 p-2 ${card.featured ? 'featured-card' : ''}`}
      >
        {/* Featured Badge - Enhanced */}
        {card.featured && (
          <motion.div
            initial={{ opacity: 0, scale: 0, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20, 
              delay: index * 0.1 + 0.3 
            }}
            className="absolute -top-1 -right-1 z-20 group/badge"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400 blur-sm opacity-75 group-hover/badge:opacity-100 transition-opacity duration-300" />
            
            {/* Main Badge */}
            <div className="relative flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-400 text-yellow-900 rounded-lg shadow-lg transform group-hover/badge:scale-105 transition-all duration-300">
              {/* Sparkle Icon */}
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="text-xs"
              >
                ✨
              </motion.div>
              
              {/* Text */}
              <span className="text-xs font-bold tracking-wide">
                {lang === 'ar' ? 'مميز' : 'HOT'}
              </span>
              
              {/* Pulse Dot */}
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-1.5 bg-yellow-900 rounded-full"
              />
            </div>
            
            {/* Corner Ribbon Effect */}
            <div className="absolute -top-1 -right-1 w-3 h-3">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-full transform rotate-45" />
            </div>
          </motion.div>
        )}
        
        {/* أيقونة الخدمة في منتصف البطاقة دائماً */}
        <div className="flex-grow flex items-center justify-center w-full">
          <div className={`${sizes.iconSize} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
            <WidgetIcon icon={card.icon} className="flex-shrink-0" />
          </div>
        </div>
        
        {/* النص في أسفل البطاقة */}
        <div className="flex-shrink-0 space-y-2 w-full">
          <h3 className={`${sizes.titleSize} font-bold transition-transform duration-300 group-hover:scale-105`}>
            {card.title}
          </h3>
          
          <p className={`${sizes.descriptionSize} opacity-90`}>
            {card.description}
          </p>
        </div>
      </motion.div>
      
      {/* تأثيرات بصرية إضافية */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10 group-hover:scale-150 transition-transform duration-500" />
      
      {/* Featured Card Special Effects */}
      {card.featured && (
        <>
          {/* Subtle glow for featured cards */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 to-orange-400/5 rounded-2xl" />
          
          {/* Animated border sparkles */}
          <motion.div
            animate={{ 
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity, 
              repeatType: 'reverse' 
            }}
            className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent bg-[length:200%_200%] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        </>
      )}
    </Link>
  );
}

export default function MainPage() {
  const { t, currentLanguage, toggleLanguage, isRTL } = useTranslation();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('darkMode');
      if (stored !== null) return stored === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const [serviceCode, setServiceCode] = useState('');
  const [showNav, setShowNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showOsama, setShowOsama] = useState(false);

  // منطق إخفاء/إظهار القائمة عند التمرير
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 50) {
        setShowNav(true);
        setLastScrollY(window.scrollY);
        return;
      }
      if (window.scrollY > lastScrollY) {
        setShowNav(false); // نزول للأسفل
      } else {
        setShowNav(true); // صعود للأعلى
      }
      setLastScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Service code mappings - يمكن للمطور تعديل هذه الروابط حسب الحاجة
  const serviceCodeMappings: { [key: string]: string } = {
    '001': '/age-calculator',
    '002': '/date-difference',
    '003': '/tafqit',
    '004': '/quick-whats',
    '005': '/qr-generator',
    '006': '/resume',
    '007': '/greeting-cards',
    '008': '/data-analytics', // محلل البيانات
    '009': '/today-date', // تاريخ اليوم
    '010': '/date-converter', // محول التاريخ
    '011': '/adhkar', // أذكاري
  };

  // Handle service code input
  const handleServiceCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and limit to 3 digits
    if (/^\d{0,3}$/.test(value)) {
      setServiceCode(value);
    }
  };

  // Handle service code submission
  const handleServiceCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (serviceCode.length === 3) {
      const targetRoute = serviceCodeMappings[serviceCode];
      if (targetRoute) {
        navigate(targetRoute);
      } else {
        // Show error message for invalid code
        alert(currentLanguage === 'ar' ? 'رمز الخدمة غير صحيح' : 'Invalid service code');
      }
    }
  };

  // Handle dark mode changes
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem('darkMode', isDarkMode.toString());
    }
  }, [isDarkMode]);

  // Add Escape key handler for modal
  useEffect(() => {
    if (!showOsama) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowOsama(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showOsama]);

  // استخدام widgetCards مباشرة بدون تصفية
  const filteredWidgetCards = widgetCards;
  
  // Ensure we have all widgets
  console.log('Widget cards:', widgetCards.map(w => ({ title: w.title, route: w.route })));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 transition-colors duration-300" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Desktop Navigation Bar */}
      <div className="hidden md:block fixed top-3 left-0 right-0 z-50 px-4 sm:px-6 md:px-8">
        <motion.nav
          initial={{ y: 0, opacity: 1 }}
          animate={showNav ? { y: 0, opacity: 1 } : { y: -80, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-4xl mx-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="flex gap-2 sm:gap-4 md:gap-6 px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full bg-gradient-to-r from-indigo-900/80 via-blue-800/70 to-indigo-600/70 backdrop-blur-lg shadow-2xl border border-white/10
            text-base sm:text-lg md:text-xl xl:text-2xl
            overflow-x-auto whitespace-nowrap justify-center items-center relative">
            
            {/* Main Navigation Items - Centered */}
            <motion.div 
              className="flex gap-2 sm:gap-4 md:gap-6 items-center justify-center"
              animate={showSettingsMenu ? { opacity: 0, scale: 0.8, x: -20 } : { opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <button
                className={`flex items-center gap-1 sm:gap-2 text-white font-bold px-3 sm:px-6 py-1.5 sm:py-2 rounded-full bg-indigo-700/80 transition text-base sm:text-lg md:text-xl flex-shrink-0
                  hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200`}
                style={{ boxShadow: undefined }}
              >
                <AiFillHome className="w-5 h-5 sm:w-6 sm:h-6" /> {t.common.home}
              </button>
              <Link 
                to="/services"
                className="flex items-center gap-1 sm:gap-2 text-white/80 hover:text-white transition flex-shrink-0 px-3 sm:px-6 py-1.5 sm:py-2 rounded-full
                  hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
                style={{ boxShadow: undefined }}
              >
                <FaTools className="w-4 h-4 sm:w-5 sm:h-5" /> {currentLanguage === 'ar' ? 'الخدمات' : 'Services'}
              </Link>
              <Link 
                to="/#"
                className="flex items-center gap-1 sm:gap-2 text-white/80 hover:text-white transition flex-shrink-0 px-3 sm:px-6 py-1.5 sm:py-2 rounded-full
                  hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
                style={{ boxShadow: undefined }}
              >
                <FaUser className="w-4 h-4 sm:w-5 sm:h-5" /> {currentLanguage === 'ar' ? 'عنّي' : 'About'}
                </Link>
            </motion.div>
            
            {/* Settings Menu - Horizontal */}
            <motion.div 
              className="absolute right-4 flex gap-2 sm:gap-4 items-center"
              initial={{ opacity: 0, scale: 0.8, x: 20 }}
              animate={showSettingsMenu ? { opacity: 1, scale: 1, x: 0 } : { opacity: 0, scale: 0.8, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Language Toggle */}
              <button 
                onClick={() => {
                  toggleLanguage();
                  setShowSettingsMenu(false);
                }}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title={currentLanguage === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
              >
                <FaGlobe className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              
              {/* Dark Mode Toggle */}
              <button 
                onClick={() => {
                  setIsDarkMode(!isDarkMode);
                  setShowSettingsMenu(false);
                }}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title={isDarkMode ? 'تفعيل الوضع الفاتح' : 'تفعيل الوضع المظلم'}
              >
                {isDarkMode ? (
                  <FaSun className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <FaMoon className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
              
              {/* Close Settings Button */}
              <button 
                onClick={() => setShowSettingsMenu(false)}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title="إغلاق الإعدادات"
              >
                <span className="text-lg font-bold">×</span>
              </button>
            </motion.div>
            
            {/* Settings Toggle Button - Absolute */}
            <motion.div 
              className="absolute right-4 settings-menu"
              animate={showSettingsMenu ? { opacity: 0, scale: 0.8 } : { opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <button 
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10"
                title="الإعدادات"
              >
                <FaCog className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </motion.div>
          </div>
        </motion.nav>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-2 pb-2">
        <motion.nav
          initial={{ y: 0, opacity: 1 }}
          animate={showNav ? { y: 0, opacity: 1 } : { y: 80, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-md mx-auto"
        >
          <div className="flex gap-2 justify-center items-center px-4 py-3 rounded-2xl bg-gradient-to-r from-indigo-900/90 via-blue-800/80 to-indigo-600/80 backdrop-blur-xl shadow-2xl border border-white/20 relative overflow-x-auto">
            {/* Main Navigation Items - Centered (مطابقة للديسكتوب) */}
            <button
              className={`flex items-center gap-1 text-white font-bold px-3 py-2 rounded-full bg-indigo-700/80 transition text-base flex-shrink-0 hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200`}
              style={{ boxShadow: undefined }}
            >
              <AiFillHome className="w-5 h-5" /> {t.common.home}
            </button>
            <Link
              to="/services"
              className="flex items-center gap-1 text-white/80 hover:text-white transition flex-shrink-0 px-3 py-2 rounded-full hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
              style={{ boxShadow: undefined }}
            >
              <FaTools className="w-5 h-5" /> {currentLanguage === 'ar' ? 'الخدمات' : 'Services'}
            </Link>
             <Link
              to="/#"
              className="flex items-center gap-1 text-white/80 hover:text-white transition flex-shrink-0 px-3 py-2 rounded-full hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
              style={{ boxShadow: undefined }}
            >
              <FaUser className="w-5 h-5" /> {currentLanguage === 'ar' ? 'عنّي' : 'About'}
              </Link>
          </div>
        </motion.nav>
      </div>

      {/* Hero Section */}
      <div className="relative flex flex-col items-center justify-center min-h-[90vh] w-full bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-600 overflow-hidden pt-20 pb-16">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Top gradient transition - 10% of section height */}
          <div className="absolute top-0 left-0 w-full h-[10%] bg-gradient-to-b from-white/90 via-indigo-50/80 to-transparent dark:from-slate-800 dark:via-indigo-900/80 dark:to-transparent pointer-events-none" />
          
          {/* Floating orbs with blur effect */}
          <motion.div
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-20 left-20 w-32 h-32 bg-purple-400/30 rounded-full blur-xl"
          />
          <motion.div
            animate={{
              x: [0, -80, 0],
              y: [0, 60, 0],
              scale: [1, 0.8, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute top-40 right-32 w-24 h-24 bg-pink-400/40 rounded-full blur-xl"
          />
          <motion.div
            animate={{
              x: [0, 60, 0],
              y: [0, -40, 0],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 4
            }}
            className="absolute bottom-32 left-1/4 w-28 h-28 bg-blue-400/35 rounded-full blur-xl"
          />
          <motion.div
            animate={{
              x: [0, -40, 0],
              y: [0, 80, 0],
              scale: [1, 0.9, 1],
            }}
            transition={{
              duration: 22,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 6
            }}
            className="absolute bottom-20 right-1/3 w-20 h-20 bg-indigo-400/45 rounded-full blur-xl"
          />
          {/* Gradient overlay for better readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/20" />
          
          {/* Bottom gradient transition - 10% of section height */}
          <div className="absolute bottom-0 left-0 w-full h-[10%] bg-gradient-to-t from-white/90 via-indigo-50/80 to-transparent dark:from-slate-800 dark:via-indigo-900/80 dark:to-transparent pointer-events-none" />
        </div>

        {/* Decorative Stars */}
        <motion.span 
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute left-10 top-32 text-white text-4xl select-none z-10"
        >
          ✦
        </motion.span>
        <motion.span 
          animate={{ 
            rotate: [360, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute right-10 top-32 text-white text-4xl select-none z-10"
        >
          ✦
        </motion.span>

        {/* Main Text */}
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-center font-extrabold text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white drop-shadow-2xl leading-tight mb-6 z-10 relative px-4"
        >
          مرحباً، أنا أسامة.
          <br />
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="block mt-4 text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-indigo-200"
          >
            محلل بيانات ، مطور ويب ، متخصص رياضيات
          </motion.span>
        </motion.h1>
        
        {/* Funny Description */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-center text-indigo-100 text-base sm:text-xl md:text-2xl lg:text-3xl mt-6 font-semibold drop-shadow-lg z-10 relative max-w-4xl mx-auto px-4"
        >
          أستطيع تحويل الأرقام إلى ضحك، والكود إلى قهوة! ☕️
          <br />
          <span className="text-indigo-200 text-sm sm:text-lg md:text-xl lg:text-2xl">
            (لا تسألني عن التكامل، ما زلت أبحث عن مشتق حياتي)
          </span>
        </motion.p>
      </div>

      {/* Service Code Input */}
      <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white/90 via-indigo-50/80 to-slate-100 dark:from-slate-800 dark:via-indigo-900/80 dark:to-slate-900 transition-colors duration-500">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mb-24 sm:mb-32 lg:mb-40"
        >
          <div className="max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6 bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-8 sm:p-12 lg:p-16">
            <form onSubmit={handleServiceCodeSubmit} className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 dark:text-white mb-4">
                  {t.common.serviceCodeTitle}
                </h2>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-600 dark:text-slate-400">
                  {t.common.serviceCodeSubtitle}
                </p>
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  value={serviceCode}
                  onChange={handleServiceCodeChange}
                  placeholder={t.common.invalidCode}
                  className="w-full rounded-3xl border-2 border-slate-300 dark:border-slate-600 py-6 sm:py-8 pl-8 pr-4 text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-widest focus:outline-none focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-800 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 transition-all duration-300 shadow-lg"
                  style={{ fontFamily: 'monospace' }}
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-slate-400 dark:text-slate-500 text-lg sm:text-xl">#</span>
                </div>
              </div>
              
              <motion.button
                type="submit"
                disabled={serviceCode.length !== 3}
                className={`w-full py-4 sm:py-6 px-8 sm:px-12 rounded-3xl font-bold text-base sm:text-lg md:text-xl lg:text-2xl text-white transition-all duration-300 shadow-lg ${
                  serviceCode.length === 3
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 cursor-pointer shadow-blue-500/25'
                    : 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed'
                }`}
                whileHover={serviceCode.length === 3 ? { scale: 1.02, y: -2 } : {}}
                whileTap={serviceCode.length === 3 ? { scale: 0.98 } : {}}
              >
                {t.common.goToService}
              </motion.button>
            </form>
            
            {/* Service Code Examples */}
            <div className="mt-12 text-center">
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mb-6">
                {t.common.serviceCodeExamples}
              </p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {Object.entries(serviceCodeMappings).slice(0, 5).map(([code]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setServiceCode(code)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm sm:text-base font-medium cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 hover:scale-105"
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Services Grid - إعادة برمجة كاملة */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-16">
        {/* Background Effects - تأثير هادئ ثابت */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900" />
        {/* عناصر زخرفية ثابتة */}
        <div className="absolute inset-0 pointer-events-none select-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-purple-200/30 rounded-full blur-2xl" />
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />
          <div className="absolute top-1/2 left-1/3 w-20 h-20 bg-pink-200/20 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 dark:text-white mb-6">
              {t.common.widgetSectionTitle}
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              {t.common.widgetSectionSubtitle}
              <button
                type="button"
                aria-label="حب"
                className="inline-flex items-center justify-center text-pink-500 hover:text-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-300 transition text-xl sm:text-2xl md:text-3xl lg:text-4xl mx-1"
                onClick={() => setShowOsama(true)}
                style={{ verticalAlign: 'middle' }}
              >
                <FaHeart />
              </button>
            </p>
          </motion.div>

          {/* Services Grid */}
          <AnimatePresence mode="wait">
            {filteredWidgetCards.length > 0 ? (
              <motion.div
                key="services-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-7xl mx-auto"
              >
                {/* ترتيب يدوي حسب التخطيط المطلوب */}
                {/* الصف الأول */}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'gift'); const config = widgetServiceConfigs.find(c => c.id === 'greeting-cards'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={0} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'resume'); const config = widgetServiceConfigs.find(c => c.id === 'resume'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={1} /> : null; })()}
                {/* الصف الثاني */}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'saudi'); const config = widgetServiceConfigs.find(c => c.id === 'saudi-dates'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={2} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'calculator'); const config = widgetServiceConfigs.find(c => c.id === 'age-calculator'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={3} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'calendar'); const config = widgetServiceConfigs.find(c => c.id === 'date-difference'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={4} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'font'); const config = widgetServiceConfigs.find(c => c.id === 'tafqit'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={5} /> : null; })()}
                {/* الصف الثالث */}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'whatsapp'); const config = widgetServiceConfigs.find(c => c.id === 'whatsapp'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={6} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'qr'); const config = widgetServiceConfigs.find(c => c.id === 'qr-generator'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={7} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'prayer'); const config = widgetServiceConfigs.find(c => c.id === 'adhkar'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={8} /> : null; })()}
                {/* الصف الرابع */}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'invoice'); const config = widgetServiceConfigs.find(c => c.id === 'invoice'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={9} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.route === '/date-converter'); const config = widgetServiceConfigs.find(c => c.id === 'date-converter'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={12} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.route === '/today-date'); const config = widgetServiceConfigs.find(c => c.id === 'today-date'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={13} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'analytics'); const config = widgetServiceConfigs.find(c => c.id === 'data-analytics'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={14} /> : null; })()}
                {(() => { const card = filteredWidgetCards.find(c => c.icon === 'more'); const config = widgetServiceConfigs.find(c => c.id === 'more'); return card && config ? <WidgetCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={15} /> : null; })()}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <FaLightbulb className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  {currentLanguage === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                </h3>
                <p className="text-gray-500 dark:text-gray-500">
                  {currentLanguage === 'ar' 
                    ? 'جرب البحث بكلمات مختلفة أو اختر فئة أخرى' 
                    : 'Try searching with different words or select another category'
                  }
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Product Showcase */}
      <MyStore />

      {/* Gradient Divider 3: Services Grid to Footer */}
      <div className="relative h-16 sm:h-20 lg:h-24 xl:h-28 overflow-hidden mb-12 sm:mb-16 lg:mb-20">
        {/* Elegant wave effect */}
       
      </div>
      
      {/* Footer - Newsletter Style */}
      <footer className="w-full flex justify-center items-center py-12 sm:py-16 px-2 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-600 rounded-3xl mx-auto mb-8 shadow-2xl transition-colors duration-500">
        <div className="w-full max-w-2xl flex flex-col items-center text-center gap-6">
          {/* Social Icons */}
          <div className="flex gap-4 justify-center mb-2">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-indigo-300 transition text-2xl" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-indigo-300 transition text-2xl" aria-label="Facebook"><i className="fab fa-facebook"></i></a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-indigo-300 transition text-2xl" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-indigo-300 transition text-2xl" aria-label="LinkedIn"><i className="fab fa-linkedin"></i></a>
          </div>
          {/* Title */}
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-2">اسامة الزبيدي</h2>
          {/* Description */}
          <p className="text-white/90 text-lg sm:text-xl mb-4 leading-relaxed">كل أداة هنا تم تطويرها بحب وإتقان، وأتمنى أن تجدوا فيها الفائدة المرجوة. لا تترددوا في التواصل معي لأي اقتراحات أو تحسينات!<a href="https://www.snapchat.com/@zk9c">💙</a>  .</p>
          {/* Email Form */}
      
        </div>
      </footer>
      
      {/* Hidden effect/modal for revealing 'أسامة الزبيدي' */}
      {showOsama && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="إظهار أسامة الزبيدي"
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl p-8 sm:p-12 flex flex-col items-center border-4 border-pink-400/40 relative focus:outline-none"
          >
            <FaHeart className="text-pink-500 text-5xl mb-4 animate-bounce" />
            <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-pink-600 dark:text-pink-300">أسامة الزبيدي</span>
            <button
              className="absolute top-3 right-3 text-xl text-slate-400 hover:text-pink-500 focus:outline-none"
              onClick={() => setShowOsama(false)}
              aria-label="إغلاق"
            >×</button>
            {/* Visually hidden close button for keyboard users */}
            <button
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
              aria-label="إغلاق"
              onClick={() => setShowOsama(false)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setShowOsama(false); }}
            >إغلاق</button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
